// 조직 초대 수락 (IMP-5)
//
// 토큰은 **URL 프래그먼트(#)** 로 온다 — 쿼리스트링은 서버 로그·Referer 에 남는다
// (소셜 콜백과 같은 원칙). 읽은 뒤 즉시 주소창에서 지운다.
//
// ⚠️ **계정이 없는 사람이 오는 경우가 기본이다.** 조직에 초대하는 사람은 대개
//    아직 우리 서비스를 쓰지 않는다. 예전에는 이 화면이 '로그인하러 가기'만 보여줬고,
//    그 버튼을 누르는 순간 프래그먼트의 토큰이 사라졌다 — 실측(운영 2026-08-24):
//    초대받은 사람이 일반 가입 절차를 밟아 **자기 개인 공간만 만들고** 끝났고,
//    초대는 대기 상태로 남았다. 그래서 토큰을 먼저 맡겨 두고(pendingInvitation),
//    가입·로그인 어느 길로 돌아와도 수락으로 이어지게 한다.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tenantApi } from "../api/tenants.ts";
import { peekInvitation, rememberInvitation, takeInvitation } from "../api/pendingInvitation.ts";
import { ApiError } from "@taplink/web-shared";
import { useAuth } from "@taplink/web-shared";

export function InvitationAcceptPage() {
  const navigate = useNavigate();
  const { me, applyToken } = useAuth();
  const [status, setStatus] = useState<"working" | "done" | "error">("working");
  const [message, setMessage] = useState("초대를 확인하는 중입니다…");
  /** 맡겨 둔 토큰이 있는가 — 미로그인 화면의 문구를 가른다 */
  const [held, setHeld] = useState(false);
  const handled = useRef(false); // StrictMode 이중 실행 방지

  // ① 토큰을 **로그인 여부와 무관하게 먼저 맡긴다.** 예전에는 로그인해야 여기까지
  //    왔고, 그래서 미로그인 사용자가 화면을 떠나면 토큰이 사라졌다.
  const stashed = useRef(false);
  useEffect(() => {
    if (stashed.current) return;
    stashed.current = true;
    const token = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token");
    if (token) {
      rememberInvitation(token);
      // 주소창에서 지운다 — 화면 공유·기록에 토큰이 남지 않게
      window.history.replaceState(null, "", window.location.pathname);
    }
    setHeld(Boolean(token) || Boolean(peekInvitation()));
  }, []);

  // ② 로그인 상태가 되면 맡긴 토큰으로 수락한다
  useEffect(() => {
    if (handled.current || !me || !stashed.current) return;
    const token = takeInvitation();
    if (!token) {
      setStatus("error");
      setMessage("초대 링크가 올바르지 않거나 만료되었습니다. 메일의 링크를 다시 눌러 주세요.");
      return;
    }
    handled.current = true;

    tenantApi
      .acceptInvitation(token)
      .then(async (tenant) => {
        // 합류 직후 그 작업 공간으로 옮겨 준다 — 수락하고 아무 일도 안 일어나면 혼란스럽다
        const issued = await tenantApi.switch(tenant.id);
        await applyToken(issued.accessToken);
        setStatus("done");
        setMessage(`${tenant.name} 에 합류했습니다.`);
        window.setTimeout(() => navigate("/", { replace: true }), 1200);
      })
      .catch((e) => {
        setStatus("error");
        setMessage(e instanceof ApiError ? e.message : "초대를 수락하지 못했습니다.");
      });
  }, [me, applyToken, navigate]);

  return (
    <main className="page">
      <div className="card">
        <h1>작업 공간 초대</h1>
        {!me ? (
          <>
            <p>
              초대를 수락하려면 로그인이 필요합니다. <strong>계정이 없으면 먼저 가입해 주세요</strong> —
              가입을 마치면 이 초대가 자동으로 이어집니다.
            </p>
            {/* ⚠️ 두 버튼을 **둘 다** 둔다. 초대받는 사람은 대개 아직 계정이 없는데
                예전에는 로그인 버튼만 있었다. */}
            <button type="button" className="btn-primary btn-block" onClick={() => navigate("/signup")}>
              가입하고 합류하기
            </button>
            <button type="button" className="btn-outline btn-block" onClick={() => navigate("/login")}>
              이미 계정이 있어요 — 로그인
            </button>
            {held ? (
              <p className="hint">초대를 기억해 두었습니다. 가입·로그인을 마치면 바로 합류합니다.</p>
            ) : (
              <p className="hint">
                초대 정보를 읽지 못했습니다. 메일의 링크를 다시 눌러 주세요.
              </p>
            )}
          </>
        ) : (
          <p role={status === "error" ? "alert" : "status"} className={status === "error" ? "alert alert-error" : undefined}>
            {message}
          </p>
        )}
      </div>
    </main>
  );
}
